import { useContext, useEffect, useReducer } from 'react'
import {
    Autocomplete,
    Avatar,
    Box,
    IconButton,
    TextField,
    Tooltip,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import {
    TipsAndUpdates as TipsAndUpdatesIcon,
    DriveFolderUpload as DriveFolderUploadIcon,
} from '@mui/icons-material'
import {
    FormSection,
    SaveButton,
    Grid,
    UniversalTagToolbar,
} from '../../../UniversalComponents'

import { ToastMessage } from '../../../../context/ToastPopUpContext'
import { Authentication } from '../../../../context/AuthenticationContext'
import {
    addEngine,
    getManufacturers,
    addEngineCalibrationFile,
    unlinkTag,
    addEngineImage,
    addTag,
} from '../../../../apiRoutes'

import responseError from '../../../../context/responseError/responseError'
import {
    basicValidator,
    parseFormWithFile,
    ParseSingularFile,
} from '../../../../utilities/functionTools'

import EngineCalibration from './EngineCalibration'

interface engineManager {
    row: any
    onClose: any
    saveCache: any
    permissionCheck: any
    quickStart: any
    blobInfo?: any
    calibrationData?: any
    deleteBlob?: any
    successfullBlobFileDeletion?: Boolean
    resetSuccessfulBlobFileDeletion?: any
}

const EngineManagerForm = ({
    row,
    onClose,
    saveCache,
    permissionCheck,
    quickStart,
    blobInfo,
    calibrationData,
    deleteBlob,
    successfullBlobFileDeletion,
    resetSuccessfulBlobFileDeletion,
}: engineManager) => {
    const setToastMessage = useContext(ToastMessage)
    const currentUser = useContext(Authentication)

    const { userProfile, activeManufacturer, user, username } = currentUser.auth
    const { superAdmin } = userProfile

    const defaultState = {
        formData: {
            engineName: row?.engineName || '',
            engineIdentifier: row?.engineIdentifier || '',
            engineDescription: row?.engineDescription || '',
            engineCalibrationFiles: row?.engineCalibrationFiles || [],
            activeEngineCalibrationFile: row?.activeEngineCalibrationFile || {},
            engineImageUrl: row?.engineImageUrl || '',
            tags: [],
            updatedBy: row?.updatedBy || '',
            id: row.id || '',
            manufacturer: row?.id
                ? row.manufacturer?.manufacturerName
                : activeManufacturer,
        },
        formValidation: {
            engineSerialValidation: false,
            hardwarePartNumber: false,
            manufacturerValidation: false,
            engineIdentifierValidation: false,
            calibrationValidation: false,
            engineNameValidation: false,
            isFormDirty: false,
            isFormValid: false,
        },
        newEngineCalibrationFiles: [],
        closeModal: false,
        manufacturers: [],
        manufacturerOptions: [],
        formCache: {},
        permissionsCheck: permissionCheck,
        etag: '',
        title: '',
        blobUrl: '',
        calData: calibrationData,
        entryId: undefined,
        successfulSubmission: false,
        selectedFile: [],
        engineImagePreview: '',
        // Calibration uploads still running. The engine cannot be saved until
        // this is 0 (see Save button below).
        uploadsInFlight: 0,
    }

    // Accepts an object or an updater function (prev => partial). Updaters let
    // async code (the calibration upload) merge into the latest state instead
    // of an outdated copy captured before the await.
    const reducer = (state, newState) => ({
        ...state,
        ...(typeof newState === 'function' ? newState(state) : newState),
    })
    const [state, setState] = useReducer(reducer, defaultState)

    const {
        formData,
        formValidation,
        manufacturerOptions,
        manufacturers,
        etag,
        title,
        blobUrl,
        calData,
        entryId,
        successfulSubmission,
        selectedFile,
        engineImagePreview,
        uploadsInFlight,
    } = state

    const {
        engineName,
        engineDescription,
        engineIdentifier,
        manufacturer,
        engineCalibrationFiles,
        activeEngineCalibrationFile,
        engineImageUrl,
    } = formData

    const {
        manufacturerValidation,
        engineNameValidation,
        engineIdentifierValidation,
        calibrationValidation,
        isFormDirty,
        isFormValid,
    } = formValidation

    const handleTagSubmit = async (tagData, removedTags) => {
        try {
            if (successfulSubmission) {
                const tag = await addTag({ tagData, entryId })

                if (tag) {
                    const { data, status } = tag

                    if (data && status === 200) {
                        console.log('Tag updated successfully')
                    } else {
                        responseError(tag, row)
                    }
                }

                if (removedTags && removedTags?.length > 1) {
                    const removeTags: any = await unlinkTag({ removedTags })

                    if (removeTags) {
                        const { data, status } = removeTags

                        if (data && status === 200) {
                            console.log('Tag removed successfully')
                        } else {
                            responseError(removeTags, row)
                        }
                    }
                }
                setState({ successfulSubmission: false })
                onClose(row.engineIdentifier, row)
            }
        } catch (err) {
            console.error(err, 'error')
            setToastMessage(`${err}`, 'error')
        }
    }

    const handleSubmit = async () => {
        try {
            const findManufacturer = user?.activeManufacturer?._id

            let engineImageUrl = ''
            let res: any

            if (selectedFile && selectedFile.length !== 0) {
                const parsedFormData = ParseSingularFile([selectedFile])
                res = await addEngineImage(parsedFormData)
                if (res) {
                    const { data, status } = res

                    if (data && status === 200) {
                        engineImageUrl = data
                    } else {
                        responseError(res, row)
                    }
                }
            }
            const newFormData = {
                ...formData,
                // An existing engine keeps its manufacturer. Using the
                // editor's active manufacturer silently moved engines to
                // whichever brand an admin had selected.
                manufacturer: row?.id
                    ? row.manufacturer?._id || findManufacturer
                    : findManufacturer,
                updatedBy: username,
                // Keep the current image unless a new one was uploaded (it
                // used to be cleared on every save).
                engineImageUrl: engineImageUrl || formData.engineImageUrl,
                // When this page loaded the engine. The API refuses the save
                // (409) if someone changed the engine after that, instead of
                // overwriting their changes with this page's older copy.
                lastLoadedUpdatedAt: row?.id ? row?.updatedAt : undefined,
            }

            res = await addEngine(newFormData)
            if (res) {
                const { data, status } = res

                if (data && status === 200) {
                    setToastMessage(
                        `Engine: ${data.engineName} was ${
                            row.id ? 'edited' : 'added'
                        } successfully`,
                        'success'
                    )

                    setState({ entryId: data?._id, successfulSubmission: true })
                } else {
                    responseError(res, row)
                }
            }
        } catch (err: any) {
            console.error(err, 'error')
            setToastMessage(`${err}`, 'error')
        }
    }

    const handleReset = () => {
        if (etag.length !== 0 && title.length !== 0 && blobUrl.length !== 0) {
            deleteBlob(true, true)
        } else {
            const clonedFiles = [...calData]
            setState({
                formData: {
                    ...defaultState.formData,
                    engineCalibrationFiles: clonedFiles,
                },
                engineImagePreview: '',
                selectedFile: '',
                formValidation: defaultState.formValidation,
            })
        }
    }

    useEffect(() => {
        if (successfullBlobFileDeletion) {
            const clonedFiles = [...calData]

            setState({
                formData: {
                    ...defaultState.formData,
                    engineCalibrationFiles: clonedFiles,
                },
                formValidation: defaultState.formValidation,
                etag: '',
                title: '',
                blobUrl: '',
            })
            resetSuccessfulBlobFileDeletion()
        }
    }, [successfullBlobFileDeletion])

    const handleChange = (e: any) => {
        const { name, value } = e.target

        setState({
            formData: { ...formData, [name]: value },
            formValidation: { ...formValidation, isFormDirty: true },
        })

        saveCache({ ...formData, [name]: value })
    }

    const handleFileSelect = (event) => {
        if (event.target.files && event.target.files.length > 0) {
            const file = event.target.files[0]

            const reader = (file, callback) => {
                const fr = new FileReader()
                fr.onload = () => callback(null, fr.result)
                fr.onerror = (err) => callback(err)
                fr.readAsDataURL(file)
            }
            reader(file, (err, res) => {
                setState({
                    engineImagePreview: res,
                    selectedFile: file,
                    formValidation: {
                        ...formValidation,
                        isFormDirty: true,
                    },
                })
            })
        }
    }

    const VisuallyHiddenInput = styled('input')({
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)',
        height: 1,
        overflow: 'hidden',
        position: 'absolute',
        bottom: 0,
        left: 0,
        whiteSpace: 'nowrap',
        width: 1,
    })

    const getData = async () => {
        let manufacturers: any = []
        let manufacturerOptions: any = []

        try {
            const getManufacturerData = await getManufacturers(true)
            if (getManufacturerData && getManufacturerData?.length !== 0) {
                manufacturers = getManufacturerData.filter(
                    (manufacturer) => manufacturer.manufacturerType === 'engine'
                )
                manufacturerOptions = manufacturers.map(
                    ({ manufacturerName }) => manufacturerName
                )
            }

            setState({
                manufacturers,
                manufacturerOptions,
            })
        } catch (err) {
            console.error(err, 'error')
            setToastMessage(`${err}`, 'error')
        }
    }

    // Adds or updates a calibration row and, when a new .emot was chosen,
    // uploads it first. Returns true when the row is in the engine's list and
    // ready to be saved with the engine, false when the upload failed (the
    // calibration dialog then stays open so the user can retry).
    //
    // The row is only added/updated after a successful upload, so the list
    // never holds a calibration without its download link and MD5. All state
    // updates use the latest state, so engine edits made while the upload is
    // running are not reverted.
    const addCalibrationFile = async (
        calibrationFormData,
        files,
        hasNewFile = true
    ) => {
        const rowId = calibrationFormData.id
        const isSameRow = (file) => file._id === rowId || file.id === rowId

        // Merge into the existing row (keeping its stored _id, so the
        // database updates it instead of recreating it) or append a new one.
        // Activating this row deactivates the others.
        const applyRow = (prev, patch) => {
            const list = prev.formData.engineCalibrationFiles || []
            const index = list.findIndex(isSameRow)
            let next =
                index !== -1
                    ? list.map((file, i) =>
                          i === index ? { ...file, ...patch } : file
                      )
                    : [...list, patch]
            if (patch.engineCalibrationActive) {
                next = next.map((file) =>
                    isSameRow(file)
                        ? file
                        : { ...file, engineCalibrationActive: false }
                )
            }
            return {
                formData: {
                    ...prev.formData,
                    engineCalibrationFiles: next,
                    ...(patch.engineCalibrationActive
                        ? { activeEngineCalibrationFile: patch }
                        : {}),
                },
                formValidation: { ...prev.formValidation, isFormDirty: true },
            }
        }

        // Details edited, no new file: nothing to upload.
        if (!hasNewFile) {
            setState((prev) => applyRow(prev, calibrationFormData))
            return true
        }

        setState((prev) => ({ uploadsInFlight: prev.uploadsInFlight + 1 }))
        try {
            const clonedFiles = new FormData()
            files.forEach((file, key) => {
                if (key === 'file') {
                    clonedFiles.append(key, file)
                }
            })
            const calibrationFile = [clonedFiles.get('file')]

            const parsedFormData = parseFormWithFile(
                {
                    ...calibrationFormData,
                    manufacturer: user?.activeManufacturer?._id,
                },
                calibrationFile
            )
            const res = await addEngineCalibrationFile(parsedFormData)
            const data = res?.status === 200 ? res.data : null

            if (!data?.engineCalibrationFileUrl || !data?.engineCalibrationMD5) {
                setToastMessage(
                    'The calibration file did not upload, so the calibration was not added. Please try again.',
                    'error'
                )
                return false
            }

            const uploadedRow = {
                ...calibrationFormData,
                engineCalibrationFileUrl: data.engineCalibrationFileUrl,
                engineCalibrationMD5: data.engineCalibrationMD5,
            }
            setState((prev) => ({
                ...applyRow(prev, uploadedRow),
                etag: data?.etag,
                title: data?.title,
                blobUrl: data?.engineCalibrationFileUrl,
            }))
            blobInfo?.({
                etag: data?.etag,
                title: data?.title,
                url: data?.engineCalibrationFileUrl,
            })
            return true
        } catch (err) {
            console.error(err, 'error')
            setToastMessage(`${err}`, 'error')
            return false
        } finally {
            setState((prev) => ({
                uploadsInFlight: Math.max(0, prev.uploadsInFlight - 1),
            }))
        }
    }

    // Never save an engine while a calibration upload is running or while a
    // calibration has no uploaded file: the device would be offered a
    // calibration it cannot download.
    const calibrationsWithoutFile = (engineCalibrationFiles || []).filter(
        (file) => !file.engineCalibrationFileUrl || !file.engineCalibrationMD5
    )
    const saveBlockedReason =
        uploadsInFlight > 0
            ? 'Waiting for the calibration file upload to finish…'
            : calibrationsWithoutFile.length !== 0
            ? `No uploaded file for: ${calibrationsWithoutFile
                  .map(
                      (file) =>
                          `rev ${file.engineCalibrationRevision || '?'} (${
                              file.engineCalibrationFileName || 'no file name'
                          })`
                  )
                  .join(
                      ', '
                  )}. Open the calibration and upload its .emot file.`
            : ''

    useEffect(() => {
        getData()
        saveCache(row)
    }, [])

    useEffect(() => {
        if (
            isFormDirty &&
            !engineIdentifierValidation &&
            !manufacturerValidation &&
            !engineNameValidation &&
            !calibrationValidation &&
            engineIdentifier &&
            manufacturer &&
            engineName &&
            engineCalibrationFiles
        ) {
            setState({
                formValidation: {
                    ...formValidation,
                    isFormValid: true,
                },
            })
        } else {
            setState({
                formValidation: {
                    ...formValidation,
                    isFormValid: false,
                },
            })
        }
    }, [
        isFormDirty,
        manufacturerValidation,
        engineNameValidation,
        engineIdentifierValidation,
        calibrationValidation,
        manufacturer,
        engineName,
        engineIdentifier,
        engineCalibrationFiles,
    ])

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sx={{ marginTop: 4, marginBottom: -3 }}>
                <UniversalTagToolbar
                    row={row}
                    pageType="engines"
                    addTag={handleTagSubmit}
                    entryId={entryId}
                    setFormDirty={() =>
                        setState({
                            formValidation: {
                                ...formValidation,
                                isFormValid: true,
                            },
                        })
                    }
                />
            </Grid>
            <Grid item xs={12} md={10}>
                <FormSection title="Engine Details">
                    <Grid item xs={12} md={3}>
                        <Autocomplete
                            disablePortal
                            id="manufacturer"
                            options={manufacturerOptions}
                            value={manufacturer}
                            fullWidth
                            disabled={true}
                            onChange={(event, newValue) => {
                                const data = {
                                    target: {
                                        name: 'manufacturer',
                                        value: newValue,
                                    },
                                }
                                handleChange(data)
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Engine Manufacturer"
                                    name="manufacturer"
                                    onChange={handleChange}
                                    required
                                    disabled={
                                        permissionCheck || superAdmin !== true
                                    }
                                    value={manufacturer}
                                    onBlur={(e: any) =>
                                        basicValidator(
                                            e,
                                            formValidation,
                                            setState
                                        )
                                    }
                                    error={manufacturerValidation}
                                    helperText={
                                        manufacturerValidation
                                            ? 'Please enter a manufacturer name.'
                                            : ''
                                    }
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            required={true}
                            fullWidth
                            label="Engine Name"
                            name="engineName"
                            onChange={handleChange}
                            value={engineName}
                            onBlur={(e: any) =>
                                basicValidator(e, formValidation, setState)
                            }
                            error={engineNameValidation}
                            helperText={
                                engineNameValidation
                                    ? 'Please enter a Engine Name.'
                                    : ''
                            }
                            disabled={permissionCheck}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            required={true}
                            fullWidth
                            label="Engine Identifier"
                            name="engineIdentifier"
                            onChange={handleChange}
                            value={engineIdentifier}
                            onBlur={(e: any) =>
                                basicValidator(e, formValidation, setState)
                            }
                            error={engineIdentifierValidation}
                            helperText={
                                engineIdentifierValidation
                                    ? 'Please enter an Engine Identifier.'
                                    : ''
                            }
                            disabled={permissionCheck}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <TextField
                            fullWidth
                            label="Engine Description"
                            name="engineDescription"
                            onChange={handleChange}
                            value={engineDescription}
                            disabled={permissionCheck}
                        />
                    </Grid>
                </FormSection>
            </Grid>
            <Grid item xs={12} md={2}>
                <FormSection title="Engine Image">
                    <Grid item xs={12} md={12}>
                        <label htmlFor="raised-button-file">
                            <Tooltip
                                title={
                                    engineImageUrl
                                        ? 'Replace Image'
                                        : 'Upload Image'
                                }
                            >
                                <center>
                                    {engineImageUrl || engineImagePreview ? (
                                        <IconButton component="label">
                                            <Box>
                                                <Avatar
                                                    src={
                                                        engineImagePreview ||
                                                        engineImageUrl
                                                    }
                                                    sx={{
                                                        width: 100,
                                                        height: 100,
                                                    }}
                                                />
                                                <VisuallyHiddenInput
                                                    onChange={(event) =>
                                                        handleFileSelect(event)
                                                    }
                                                    type="file"
                                                    accept="image/*"
                                                    id='htmlFor="raised-button-file'
                                                    disabled={permissionCheck}
                                                />
                                            </Box>
                                        </IconButton>
                                    ) : (
                                        <IconButton component="label">
                                            <DriveFolderUploadIcon
                                                sx={{
                                                    fontSize: 40,
                                                }}
                                            />
                                            <VisuallyHiddenInput
                                                onChange={(event) =>
                                                    handleFileSelect(event)
                                                }
                                                type="file"
                                                accept="image/*"
                                                id='htmlFor="raised-button-file'
                                                disabled={permissionCheck}
                                            />
                                        </IconButton>
                                    )}
                                </center>
                            </Tooltip>
                        </label>
                    </Grid>
                </FormSection>
            </Grid>

            <Grid item xs={12}>
                <FormSection
                    title="Engine Calibration"
                    titleBottom={
                        <Grid item xs={12}>
                            <TipsAndUpdatesIcon
                                sx={{
                                    fontSize: 20,
                                }}
                            />
                            Please save the form for any calibration file
                            changes to take effect
                        </Grid>
                    }
                    titleBottomAlignment="left"
                >
                    <Grid item xs={12}>
                        <EngineCalibration
                            engineCalibrationFiles={engineCalibrationFiles}
                            activeEngineCalibrationFile={
                                activeEngineCalibrationFile
                            }
                            addCalibrationFile={addCalibrationFile}
                        />
                    </Grid>
                </FormSection>
            </Grid>
            <Grid item xs={12} md={12}>
                {saveBlockedReason && (
                    <Box
                        sx={{
                            textAlign: 'center',
                            color: 'warning.main',
                            fontSize: 'small',
                            marginBottom: 1,
                        }}
                    >
                        {saveBlockedReason}
                    </Box>
                )}
                <SaveButton
                    title="Required Fields Are Marked With An *"
                    manufacturer={row.manufacturerName}
                    titleAlignment="center"
                    handleSubmit={handleSubmit}
                    handleReset={handleReset}
                    disabledSave={
                        !isFormValid || permissionCheck || !!saveBlockedReason
                    }
                    disabledReset={!isFormDirty || permissionCheck}
                    quickStart={quickStart}
                    resetVariant="contained"
                />
            </Grid>
        </Grid>
    )
}

export default EngineManagerForm
